import { useState, useContext } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests';
import { getStatusCodeGroup } from '@atlassian/jira-project-custom-roles/src/services/utils/index.tsx';
import type { RoleId, RoleTypeId, BaseUrl, ProjectKey, ActorType } from '../../common/types';
import { isUser } from '../../common/utils';
import AccessPageContext from '../access-page-context';
import type { AddActorsRequestData, Props } from './types';

export const addActorsToRoleUrl = (
	baseUrl: BaseUrl,
	projectKey: ProjectKey,
	roleId: RoleId,
): string => `${baseUrl}/rest/projectconfig/latest/roles/${projectKey}/${roleId}`;

export const addActorsToRoleUrlOptions = (actorNames: AddActorsRequestData) => {
	const data = JSON.stringify(actorNames);
	return {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
		},
		body: data,
	};
};

export const AddActorsToRole = (props: Props) => {
	const { children, onAddActorToRoleError } = props;
	const [error, setError] = useState<unknown>();
	const { baseUrl, projectKey } = useContext(AccessPageContext);

	const addActorToRole = async (
		{
			addedRoleId,
			roleTypeId,
			type,
		}: {
			addedRoleId: RoleId;
			roleTypeId: RoleTypeId;
			type: ActorType;
		}, // eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<any> => {
		const requestData: AddActorsRequestData = {
			users: isUser(type) ? [roleTypeId] : [],
			groups: !isUser(type) ? [roleTypeId] : [],
		};
		try {
			await performPostRequest(addActorsToRoleUrl(baseUrl, projectKey, addedRoleId), {
				body: JSON.stringify(requestData),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (httpError: any) {
			setError(httpError.message);
			onAddActorToRoleError(addedRoleId, roleTypeId, type);
			fireErrorAnalytics({
				meta: {
					id: 'addActorToRole',
					packageName: 'jiraUsersAndRoles',
					teamName: 'olympus',
				},
				error: httpError,
				attributes: {
					addedRoleId,
					roleTypeId,
					type,
					statusCode: getStatusCodeGroup(httpError),
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

	return children({
		error,
		onAddActorToRole: addActorToRole,
	});
};

export default AddActorsToRole;
